import React, { useEffect, useMemo, useState } from "react";
import { Select, Modal, Form, Input, message } from "antd";
import { apiService } from "@/services/api";

/** Langues proposées d'emblée, africaines d'abord puis internationales. */
const LANGUES_DE_BASE: Array<{ code: string; nom: string }> = [
  { code: "fr", nom: "Français" },
  { code: "en", nom: "Anglais" },
  { code: "wo", nom: "Wolof" },
  { code: "bm", nom: "Bambara" },
  { code: "ff", nom: "Peul" },
  { code: "ha", nom: "Haoussa" },
  { code: "yo", nom: "Yoruba" },
  { code: "sw", nom: "Swahili" },
  { code: "ar", nom: "Arabe" },
  { code: "pt", nom: "Portugais" },
  { code: "es", nom: "Espagnol" },
  { code: "de", nom: "Allemand" },
  { code: "it", nom: "Italien" },
  { code: "ja", nom: "Japonais" },
  { code: "ko", nom: "Coréen" },
  { code: "zh", nom: "Chinois" },
];

const VALEUR_AUTRE = "__autre__";

/**
 * Choix de la langue d'origine, avec ajout d'une langue absente de la liste.
 *
 * La liste était écrite en dur : une langue absente ne pouvait pas être
 * choisie. Deux compléments :
 *
 * - les langues **déjà employées** par des contenus sont fusionnées avec la
 *   liste de base, si bien qu'une langue ajoutée une fois reste disponible pour
 *   tous — sans table ni écran de gestion à maintenir ;
 * - « Autre langue » demande le **code ISO 639** en plus du nom. Ce champ n'est
 *   pas une étiquette : sa valeur part dans les métadonnées FFmpeg et dans le
 *   manifeste HLS. Y écrire « Bambara » produirait `LANGUAGE="bambara"`, que
 *   les lecteurs ne savent pas interpréter.
 */
export const SelecteurLangue: React.FC<{
  value?: string;
  onChange?: (valeur: string | undefined) => void;
}> = ({ value, onChange }) => {
  const [ajoutees, setAjoutees] = useState<Array<{ code: string; nom: string }>>(
    []
  );
  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    apiService
      .getUsedLanguages()
      .then((codes) => {
        const connus = new Set(LANGUES_DE_BASE.map((l) => l.code));
        setAjoutees(
          codes
            .filter((c) => c && !connus.has(c))
            .map((c) => ({ code: c, nom: c }))
        );
      })
      .catch(() => setAjoutees([]));
  }, []);

  const options = useMemo(() => {
    const toutes = [...LANGUES_DE_BASE, ...ajoutees];
    // Une valeur déjà enregistrée sur le contenu doit rester sélectionnable,
    // même si l'appel des langues utilisées a échoué.
    if (value && !toutes.some((l) => l.code === value)) {
      toutes.push({ code: value, nom: value });
    }
    return toutes;
  }, [ajoutees, value]);

  const confirmerAjout = async () => {
    const valeurs = await form.validateFields();
    const code = String(valeurs.code).trim().toLowerCase();
    const nom = String(valeurs.nom).trim();

    if (options.some((l) => l.code === code)) {
      message.warning("Cette langue figure déjà dans la liste");
      onChange?.(code);
      setDialogueOuvert(false);
      return;
    }

    setAjoutees((precedentes) => [...precedentes, { code, nom }]);
    onChange?.(code);
    setDialogueOuvert(false);
    form.resetFields();
    message.success(
      `« ${nom} » sélectionnée. Elle rejoindra la liste dès l'enregistrement du contenu.`
    );
  };

  return (
    <>
      <Select
        showSearch
        placeholder="Sélectionner la langue originale"
        value={value}
        optionFilterProp="label"
        onChange={(v) => {
          if (v === VALEUR_AUTRE) {
            setDialogueOuvert(true);
            return;
          }
          onChange?.(v);
        }}
        options={[
          ...options.map((l) => ({
            value: l.code,
            label: l.nom === l.code ? l.code : `${l.nom} (${l.code})`,
          })),
          { value: VALEUR_AUTRE, label: "➕ Autre langue…" },
        ]}
      />

      <Modal
        title="Ajouter une langue"
        open={dialogueOuvert}
        onCancel={() => setDialogueOuvert(false)}
        onOk={confirmerAjout}
        okText="Ajouter"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nom"
            label="Nom de la langue"
            rules={[{ required: true, message: "Indiquez le nom" }]}
          >
            <Input placeholder="ex. Sérère" />
          </Form.Item>
          <Form.Item
            name="code"
            label="Code ISO 639"
            tooltip="Deux ou trois lettres. Cette valeur part dans le manifeste vidéo : un code inventé ne serait pas compris par les lecteurs."
            rules={[
              { required: true, message: "Indiquez le code" },
              {
                pattern: /^[a-zA-Z]{2,3}$/,
                message: "Deux ou trois lettres, sans accent (ex. srr)",
              },
            ]}
          >
            <Input placeholder="ex. srr" maxLength={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SelecteurLangue;
